import { useState } from 'react'
import { Cake, Building2, MapPin, CalendarDays, ListFilter, Award, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Aniversariantes do mês e marcos de tempo de casa dos funcionários ativos

interface Pessoa { motcod: string; nome: string; cargo: string; data: string; dia: number; anos: number }

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const MARCOS = [1, 5, 10, 15, 20, 25, 30, 35, 40]
const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')

export default function RelatorioAniversariantes() {
  const { empresas, empresaCod } = useEmpresa()
  const hoje = new Date()
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [filial, setFilial] = useState('')
  const [tipo, setTipo] = useState<'A' | 'T'>('A')
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [ano, setAno] = useState(hoje.getFullYear())
  const [marcos, setMarcos] = useState<number[]>([1, 5, 10, 15, 20, 25, 30])
  const [res, setRes] = useState<Pessoa[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  const params = () => ({ empresas: empresasSel.join(','), filial, tipo, mes, ano, marcos: marcos.join(','), empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/aniversariantes', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/aniversariantes', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'aniversariantes.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      let msg = formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.'
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerando('')
    }
  }

  const alternarMarco = (m: number) => setMarcos(l => (l.includes(m) ? l.filter(x => x !== m) : [...l, m].sort((a, b) => a - b)))
  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Cake size={18} color="#fff" />
            </span>
            <div>
              <h2>Aniversariantes e Tempo de Casa</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Funcionários ativos que fazem aniversário ou completam tempo de empresa no mês</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Estabelecimento" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>

          <Linha label="Filial" icon={MapPin}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input value={filial} onChange={e => setFilial(e.target.value)} maxLength={2} style={{ maxWidth: 70 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todas</span>
            </div>
          </Linha>

          <Linha label="Listar" icon={ListFilter}>
            <select value={tipo} onChange={e => setTipo(e.target.value as 'A' | 'T')} style={{ maxWidth: 240 }}>
              <option value="A">Aniversariantes do mês</option>
              <option value="T">Tempo de casa</option>
            </select>
          </Linha>

          <Linha label="Mês / Ano" icon={CalendarDays}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ maxWidth: 160 }}>
                {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <input type="number" value={ano} onChange={e => setAno(Number(e.target.value))} min={1900} max={2200} style={{ maxWidth: 90 }} />
            </div>
          </Linha>

          {tipo === 'T' && (
            <Linha label="Marcos (anos)" icon={Award}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.9rem' }}>
                {MARCOS.map(m => (
                  <label key={m} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={marcos.includes(m)} onChange={() => alternarMarco(m)} /> {m}
                  </label>
                ))}
              </div>
            </Linha>
          )}

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum funcionário no período.</div>}

            {res.length > 0 && (
              <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={th}>Dia</th><th style={th}>Código</th><th style={th}>Funcionário</th><th style={th}>Cargo/Função</th>
                      <th style={th}>{tipo === 'A' ? 'Nascimento' : 'Admissão'}</th><th style={th}>{tipo === 'A' ? 'Idade' : 'Anos de casa'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.map((l, i) => (
                      <tr key={l.motcod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                        <td style={{ ...td, fontWeight: 700, color: '#1b3f7b' }}>{String(l.dia).padStart(2, '0')}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td>
                        <td style={{ ...td, fontWeight: 600 }}>{l.nome}</td>
                        <td style={td}>{l.cargo}</td>
                        <td style={td}>{dataBr(l.data)}</td>
                        <td style={{ ...td, fontWeight: 700 }}>{l.anos}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
              TOTAL DE REGISTROS: {res.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
